<template>
  <div>
    <div class="pushtop" />
    <div class="feature_product_area petadd-wrapper">
      <div class="main_box d-block">
        <!-- Loading state -->
        <div
          v-if="authLoading"
          class="text-center py-5"
        >
          <div
            class="spinner-border text-primary"
            role="status"
          >
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <!-- Not logged in state -->
        <div
          v-else-if="!isLoggedIn"
          class="text-center py-5"
        >
          <h3 class="mb-4">
            You must be logged in to post an ad
          </h3>
          <p class="text-muted mb-4">
            Please log in or register to continue.
          </p>
          <router-link
            to="/login?redirect=/add-listing"
            class="btn btn-primary btn-lg"
          >
            Login / Register
          </router-link>
        </div>

        <!-- Logged in state -->
        <div v-else>
          <!-- Limit Reached state -->
          <div
            v-if="!hasAdLimit && !showProfileModal"
            class="text-center py-5"
          >
            <h3 class="mb-4">
              Ad Limit Reached
            </h3>
            <p class="text-muted mb-4">
              You have reached your limit for posting new ads. Please upgrade your membership or contact support to increase your limit.
            </p>
            <router-link
              to="/profile"
              class="btn btn-primary btn-lg"
            >
              Go to Profile
            </router-link>
          </div>

          <!-- Selection Container (hidden if modal is open or limit reached) -->
          <div
            v-else-if="hasAdLimit"
            v-show="!showProfileModal"
            class="selection-container"
          >
            <div class="button-group">
              <!-- Normal button for Pet Listing selection -->
              <button
                class="selection-button"
                :class="{ active: selectedListingType === 'pet' }"
                @click="selectedListingType = 'pet'"
              >
                Pet Listing
              </button>
              <!-- Normal button for Service Listing selection -->
              <button
                class="selection-button"
                :class="{ active: selectedListingType === 'service' }"
                @click="selectedListingType = 'service'"
              >
                Service Listing
              </button>
            </div>
          </div>
          
          <div
            v-show="hasAdLimit && !showProfileModal"
            class="w-100"
          >
            <AddPetListing v-if="selectedListingType === 'pet'" />
            <AddServiceListing v-if="selectedListingType === 'service'" />
          </div>
        </div>
      </div>
    </div>
    
    <!-- Complete Profile Modal -->
    <CompleteProfileModal 
      v-if="showProfileModal" 
      @completed="handleProfileCompleted" 
    />
  </div>
</template>

<script setup>
  import { ref, watchEffect, onMounted, onUnmounted } from 'vue';
  import { getAuth, onAuthStateChanged } from 'firebase/auth';
  import { useUserStore } from '@/stores/userStore';
  import AddPetListing from '@/components/AddPetListing.vue';
  import AddServiceListing from '@/components/AddServiceListing.vue';
  import CompleteProfileModal from '@/components/CompleteProfileModal.vue';

  const userStore = useUserStore();
  const selectedListingType = ref('pet'); // Default to pet listing
  const showProfileModal = ref(false);
  const authLoading = ref(true);
  const isLoggedIn = ref(false);
  const hasAdLimit = ref(true);
  
  const checkProfileCompletion = () => {
    if (isLoggedIn.value && userStore.userProfile) {
      const u = userStore.userProfile;
      const properties = u.account?.properties;
      const limit = properties?.ad_limit !== undefined ? properties.ad_limit : 3;
      
      hasAdLimit.value = limit > 0;

      if (!u.displayName || !u.postalCode || !u.latitude || !u.longitude) {
        showProfileModal.value = true;
      } else {
        showProfileModal.value = false;
      }
    }
  };

  let unsubscribeAuth = null;

  onMounted(() => {
    const auth = getAuth();
    unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      isLoggedIn.value = !!currentUser;
      
      if (currentUser) {
        // Force fetch fresh user profile data to ensure postal code and limits are up to date
        userStore.isProfileLoaded = false;
        await userStore.fetchUserProfile(currentUser.uid);
      }
      
      authLoading.value = false;
      checkProfileCompletion();
    });
  });

  onUnmounted(() => {
    if (unsubscribeAuth) unsubscribeAuth();
  });

  watchEffect(() => {
    checkProfileCompletion();
  });

  const handleProfileCompleted = () => {
    showProfileModal.value = false;
  };
</script>

<style scoped>
  @media (min-width: 992px) {
    .pushtop {
      min-height: 110px;
    }
  }
  @media (min-width: 1200px) {
    .pushtop {
      min-height: 175px;
    }
  }
  .selection-container {
    margin: 20px 0;
    padding: 15px;
    max-width: 600px;
    margin: 0 auto;
    background: #f5f5f5;
    border-radius: 8px;
    margin-top: 20px;
  }

  .button-group {
    display: flex;
    gap: 12px;
  }

  .selection-button {
    padding: 10px 20px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: white;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .selection-button.active {
    background: #1976d2;
    color: white;
    border-color: #1976d2;
  }

  .selection-button:hover {
    background: #e3f2fd;
  }
</style>
